import { Icon } from '@mdvip/shared/components/blocks/Icon';
import { ToggleGroup, ToggleGroupItem } from '@mdvip/shared/components/ui/toggle-group';
import { useTranslation } from 'react-i18next';
import type { BillingAddressValues, PaymentTab } from '@/lib/payments/paymentAccount.types';
import { BankAccountFields } from './BankAccountFields';
import { BillingInformationSection } from './BillingInformationSection';
import { CreditCardFields } from './CreditCardFields';

export interface PaymentMethodFormProps {
  activeTab: PaymentTab;
  onTabChange: (tab: PaymentTab) => void;
  /** When the member is already enrolled, the AutoPay checkbox is locked on. */
  autoPayEnrolled: boolean;
  /** Opened from AutoPay enrollment — also locks AutoPay on, badged "Enrolling" instead of silent. */
  fromAutopay?: boolean;
  savedAddress: BillingAddressValues | null;
  billingFormOpen: boolean;
  onToggleBillingForm: () => void;
}

/**
 * Screen 1 of the Add Payment Account modal: the Credit Card / Bank Account toggle, the active
 * field group, and the billing section. Consumes the shared form via context; the orchestrator
 * owns screen state, the form instance, and the footer actions (Cancel / Review).
 */
export function PaymentMethodForm({
  activeTab,
  onTabChange,
  autoPayEnrolled,
  fromAutopay,
  savedAddress,
  billingFormOpen,
  onToggleBillingForm,
}: PaymentMethodFormProps) {
  const { t } = useTranslation('members');
  const accountKind = activeTab === 'credit-card' ? 'card' : 'bank';

  return (
    <div className="space-y-6">
      <ToggleGroup
        variant="segmented"
        className="w-full [&>button]:py-3 [&>button]:text-sm [&>button]:font-bold"
        value={[activeTab]}
        onValueChange={(values) => {
          const next = values[0];
          if (next) onTabChange(next as PaymentTab);
        }}
      >
        <ToggleGroupItem value="credit-card">
          <Icon name="creditCard" /> {t('payments.addAccount.tabs.creditCard')}
        </ToggleGroupItem>
        <ToggleGroupItem value="bank-account">
          <Icon name="landmark" /> {t('payments.addAccount.tabs.bankAccount')}
        </ToggleGroupItem>
      </ToggleGroup>

      {activeTab === 'credit-card' ? (
        <CreditCardFields autoPayEnrolled={autoPayEnrolled} fromAutopay={fromAutopay} />
      ) : (
        <BankAccountFields autoPayEnrolled={autoPayEnrolled} fromAutopay={fromAutopay} />
      )}

      <BillingInformationSection
        accountKind={accountKind}
        savedAddress={savedAddress}
        formOpen={billingFormOpen}
        onToggleForm={onToggleBillingForm}
      />
    </div>
  );
}
